<script setup>
import { Head } from '@inertiajs/vue3';
import Header from '@/Components/Header.vue';
import Footer from '@/Components/Footer.vue';
import { ref, inject } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { Switch } from '@headlessui/vue';

// Cookie handling logic
const showEssential = ref(false);
const showAnalytics = ref(false);
const showCookie = ref(false);
const showCookieSettings = ref(false);
const acceptAnalytics = ref(false);

// Check if cookies need to be shown
const $cookies = inject('$cookies');
if ($cookies.get('cookie-consent') === undefined || $cookies.get('cookie-consent') === null) {
    showCookie.value = true;
} else {
    showCookie.value = false;
}

function acceptCookies() {
    $cookies.set('cookie-consent', 'true', "1y");
    showCookie.value = false;
    showCookieSettings.value = false;
    if (acceptAnalytics.value) {
        $cookies.set('cookie-analytics', 'true', "1y");
        setCookiesAsScripts();
    }
}

function acceptAllCookies() {   
    $cookies.set('cookie-consent', 'true', "1y");
    $cookies.set('cookie-analytics', 'true', "1y");
    setCookiesAsScripts();
    showCookie.value = false;
    showCookieSettings.value = false;
}

function setCookiesAsScripts() {
    if (typeof gtag === 'function') {
        gtag('consent', 'update', {
            'ad_storage': $cookies.get('cookie-analytics') == "true" ? 'granted' : 'denied',
            'analytics_storage': $cookies.get('cookie-analytics') == "true" ? 'granted' : 'denied',
            'ad_user_data': $cookies.get('cookie-analytics') == "true" ? 'granted' : 'denied',
            'ad_personalization': $cookies.get('cookie-analytics') == "true" ? 'granted' : 'denied',
        });
    }
}
</script>

<template>
    <Head title="Algemene voorwaarden | mijnautosnelweg.nl">
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
        <link rel="manifest" href="/site.webmanifest">
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#ff5a22">
        <meta name="msapplication-TileColor" content="#ff5a22">
        <meta name="theme-color" content="#ffffff">
        <meta property="og:url" content="https://mijnautosnelweg.nl">
        <meta property="og:type" content="website">
        <meta property="og:title" content="Algemene voorwaarden | mijnautosnelweg.nl">
        <meta property="og:description" content="Jouw route naar autoverkoop!">
        <meta property="og:image" content="/img/og_image.png">
        <meta name="twitter:card" content="summary_large_image">
        <meta property="twitter:domain" content="mijnautosnelweg.nl">
        <meta property="twitter:url" content="https://mijnautosnelweg.nl">
        <meta name="twitter:title" content="Algemene voorwaarden | mijnautosnelweg.nl">
        <meta name="twitter:description" content="Jouw route naar autoverkoop!">
        <meta name="twitter:image" content="/img/og_image.png">
    </Head>
        
    <!-- Pass isInnerPage prop to Header -->
    <Header :isInnerPage="true" />

    <div class="relative mt-40">
        <div class="p-4 flex flex-col mt-20 mx-auto max-w-4xl">
            <div class="flex flex-col gap-2 mb-12">
                <h1 class="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-royalblue-500 text-balance">Algemene Voorwaarden mijnautosnelweg.nl B.V.</h1>
                <p class="text-base">Deze algemene voorwaarden zijn van toepassing op – en maken onlosmakelijk deel uit van - alle diensten van mijnautosnelweg.nl B.V., gevestigd te Slot 6, 7608 ND Almelo, ingeschreven bij de Kamer van Koophandel onder nummer 94764352. Door gebruik te maken van onze diensten gaat u akkoord met deze voorwaarden.</p>
            </div>
            <ol class="list-decimal list-inside mb-4 text-shadow">
                <h2 class="text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 1. Toepasselijkheid</h2>
                <li>
                <ol>
                    <li>Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, offertes, overeenkomsten en diensten van mijnautosnelweg.nl B.V., tenzij uitdrukkelijk en schriftelijk anders is overeengekomen.</li>
                    <li>Door gebruik te maken van de diensten van mijnautosnelweg.nl B.V. verklaart de gebruiker de algemene voorwaarden te hebben gelezen, begrepen en hiermee akkoord te gaan.</li>
                    <li>Afwijken van deze voorwaarden is alleen mogelijk indien dit schriftelijk door beide partijen is bevestigd.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 2. Definities</h2>
                <li>
                <ol>
                    <li>Verkoper: Iedere (rechts)persoon die via mijnautosnelweg.nl B.V. een voertuig aanbiedt voor verkoop.</li>
                    <li>Door gebruik te maken van de diensten van mijnautosnelweg.nl B.V. verklaart de gebruiker de algemene voorwaarden te hebben gelezen, begrepen en hiermee akkoord te gaan.</li>
                    <li>Afwijken van deze voorwaarden is alleen mogelijk indien dit schriftelijk door beide partijen is bevestigd.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 3. Het Bod</h2>
                <li>
                <ol>
                    <li>Verkoper ontvangt via mijnautosnelweg.nl B.V. een bod gebaseerd op de door hem/haar verstrekte voertuiggegevens. De verkoper is verantwoordelijk voor de juistheid van de verstrekte informatie.</li>
                    <li>Indien de voertuiggegevens onjuist of onvolledig zijn en de daadwerkelijke staat van het voertuig afwijkt van de opgegeven omschrijving, behoudt de koper het recht om het bod in te trekken of te herzien.</li>
                    <li>Zodra de verkoper het bod accepteert, komt er een verkoopovereenkomst tot stand en is de verkoper verplicht het voertuig aan de koper te leveren onder de overeengekomen voorwaarden.</li>
                    <li>Het is de koper niet toegestaan om tijdens de aflevering opnieuw over de prijs te onderhandelen, mits de voertuigomschrijving naar waarheid is ingevuld.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 4. Totstandkoming Verkoopovereenkomst</h2>
                <li>
                <ol>
                    <li>De verkoopovereenkomst komt tot stand zodra de verkoper het bod aanvaardt, digitaal of schriftelijk.</li>
                    <li>De koper is verplicht het voertuig over te nemen en de overeengekomen prijs te betalen, tenzij het voertuig niet overeenkomt met de door de verkoper opgegeven omschrijving.</li>
                    <li>De verkoper moet binnen 1 dag na acceptatie van het bod contact opnemen met de koper om een afspraak te maken voor de aflevering van het voertuig.</li>
                    <li>Als de verkoper geen contact kan krijgen met de koper, moet hij/zij dit direct melden aan mijnautosnelweg.nl B.V.</li>
                    <li>De aflevering van het voertuig dient binnen 14 dagen na het tot stand komen van de verkoopovereenkomst te plaatsvinden, tenzij anders overeengekomen.</li>
                    <li>Indien de koper constateert dat het voertuig niet voldoet aan de opgegeven voertuiggegevens, heeft de koper het recht om een aangepast bod te doen of de verkoopovereenkomst te ontbinden.</li>
                    <li>De verkoper kan de verkoop alleen annuleren indien het voertuig daadwerkelijk afwijkt van de opgegeven informatie. Bij annulering om andere redenen is de verkoper aansprakelijk voor eventuele kosten of boetes.</li>
                    <li>Bij overtreding van de aflevertermijn of verkoop aan een derde partij is de verkoper in gebreke en wordt een boete van 15% van het bod, met een minimum van €250 exclusief BTW, opgelegd.</li>
                    <li>De koper is verplicht het voertuig binnen de afgesproken termijn op te halen of af te nemen, anders wordt hij/zij in gebreke gesteld en kan eenzelfde boete worden opgelegd.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 5. Betaling
                </h2>
                <li>
                <ol>
                    <li>Voor de verkoper zijn er geen kosten verbonden aan de diensten van mijnautosnelweg.nl B.V.</li>
                    <li>De koper is verplicht om de volledige koopsom bij aflevering - na het vrijwaren van het voertuig -direct per bank of contant te betalen. Bij een bankoverschrijving dient de koper een betalingsbewijs te overleggen. </li>
                    <li>Koper dient de factuur voor de door mijnautosnelweg.nl B.V. geleverde diensten binnen 14 dagen na factuurdatum te voldoen.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 6. Aflevering</h2>
                <li>
                <ol>
                    <li>Voor de betaling heeft de koper het recht om het voertuig te inspecteren om te controleren of het overeenkomt met de opgegeven omschrijving.</li>
                    <li>Indien het voertuig niet overeenkomt met de door de verkoper opgegeven omschrijving, heeft de koper het recht om een aangepast bod te doen of de verkoop te ontbinden.</li>
                    <li>De verkoper dient het voertuig te overhandigen met een geldig legitimatiebewijs en de koper dient een vrijwaringsbewijs te verstrekken.</li>
                    <li>Na de overdracht is de verkoper niet langer verantwoordelijk voor eventuele garantieaanspraken.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 7. Privacy</h2>
                <li>
                <ol>
                    <li>mijnautosnelweg.nl B.V. respecteert de privacy van haar gebruikers en handelt in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG).</li>
                    <li>Persoonsgegevens worden alleen gebruikt voor het uitvoeren van de dienst en worden niet verstrekt aan derden zonder toestemming van de gebruiker.</li>
                    <li>Gebruikers hebben het recht om inzage te vragen in hun gegevens en om correctie of verwijdering te verzoeken.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 8. Intellectuele Eigendom</h2>
                <li>
                <ol>
                    <li>Alle intellectuele eigendomsrechten met betrekking tot de website en diensten van mijnautosnelweg.nl B.V. blijven eigendom van mijnautosnelweg.nl B.V.</li>
                    <li>Het is verboden om zonder schriftelijke toestemming informatie, tekst of materiaal van de website te kopiëren, reproduceren of te verspreiden.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 9. Aansprakelijkheid</h2>
                <li>
                <ol>
                    <li>mijnautosnelweg.nl B.V. is niet aansprakelijk voor schade die ontstaat door het gebruik van de website of diensten.</li>
                    <li>De verantwoordelijkheid voor de juistheid van de voertuiggegevens ligt volledig bij de verkoper.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 10. Klachten</h2>
                <li>
                <ol>
                    <li>Klachten over de dienstverlening kunnen per e-mail worden ingediend via hallo@mijnautosnelweg.nl.</li>
                    <li>Wij streven ernaar om klachten binnen 14 dagen in behandeling te nemen en een passende oplossing te bieden.</li>
                </ol>
                </li>
                <h2 class="pt-8 text-lg sm:text-xl md:text-2xl text-royalblue-500 font-normal mb-4">Artikel 11. Toepasselijk Recht</h2>
                <li>
                <ol>
                    <li>Op alle overeenkomsten en geschillen tussen gebruikers en mijnautosnelweg.nl B.V. is het Nederlands recht van toepassing.</li>
                    <li>Geschillen zullen worden voorgelegd aan de bevoegde rechter in Nederland.</li>
                </ol>
                </li>
            </ol>
        </div>


        <div class="p-4 relative">
            <div class="max-w-4xl mx-auto pt-12">
                <div class="flex flex-col gap-8 bg-white rounded-2xl p-8 shadow-2xl shadow-deepblue/40">
                    <div class="flex flex-col gap-6">
                        <h2 class="text-lg sm:text-xl md:text-2xl text-royalblue-500 font-bold mb-8">Wilt u ons spreken, is er iets onduidelijk of hebt u een andere vraag? </h2>
                        <p class="mb-2 pb-2 border-b border-royalblue-500/20 text-xl">mijnautosnelweg.nl B.V.</p>
                        <div class="grid grid-cols-1 md:grid-cols-2">
                            <p class="md:order-1 mb-2">Slot 6, 7608 ND Almelo</p>
                            <p class="md:order-3 mb-2">KvK-nummer: 94764352</p>
                            <p class="md:order-2 mb-2">E-mail: hallo@mijnautosnelweg.nl</p>
                            <p class="md:order-4 mb-2">Telefoon: 085-0477766</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="absolute h-full w-full inset-0 -z-40 flex flex-col">
                <div class="h-fit flex grow w-full bg-white"></div>
                <div class="h-4/5 w-full bg-gradient-to-b from-transparent to-royalblue-500 via-lessbrightorange via-35%"></div>
            </div>
        </div>
    </div>

    <!-- Use the Footer component -->
    <Footer />

    <!-- Cookie consent dialog -->
    <div v-if="showCookie" class="fixed bottom-0 left-0 right-0 border-t-2 border-royalblue-500 bg-white w-full flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-3">
        <p class="text-balance text-center md:text-left">Mijnautosnelweg maakt gebruik van cookies om onze website te verbeteren, maar alleen met jouw toestemming. Bij instellingen lees je meer over hoe het werkt.</p>
        <div class="flex flex-row gap-4 items-center">
            <a @click="showCookieSettings = true;" class="group transition-all duration-300 ease-in-out inline-flex items-center border-b-2 border-transparent pt-1 text-base font-base text-royalblue-500 hover:text-royalblue-500/80 cursor-pointer">
                <span class="bg-left-bottom bg-gradient-to-r from-brightorange to-brightorange bg-[length:0%_2px] bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out">
                    Instellingen
                </span>
            </a>
            <a @click="acceptAllCookies" class="primary-btn cursor-pointer">Accepteren</a>
        </div>
    </div>

    <!-- Cookie settings modal -->
    <TransitionRoot as="template" :show="showCookieSettings">
        <Dialog as="div" class="relative z-50">
            <div class="fixed left-0 right-0 top-0 bottom-0 z-[100] w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-clip rounded-lg bg-white border shadow-deepblue/40 shadow-3xl px-4 pb-4 pt-5 text-left transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                            <div
                                class="flex flex-col gap-4 p-4 overflow-y-auto  max-h-[512px] sm:max-h-[628px] md:max-h-[745px]">
                                                <div class="flex justify-between items-center mb-4 rounded-t sm:mb-5">
                                    <DialogTitle>
                                        <img class="h-8 w-auto mt-2" src="/public/img/logo_full.svg"
                                            alt="mijnautosnelweg" />
                                    </DialogTitle>
                                </div>
                                <form action="#">
                                    <div class="mb-4 border-b">
                                        <h4 class="block mb-2 text-lg font-semibold text-gray-900 ">Cookie instellingen
                                        </h4>
                                        <p class="block mb-4 text-sm text-gray-900 ">Om deze Diensten te gebruiken,
                                            hebben we uw toestemming nodig. Door op “Alles accepteren” te klikken,
                                            verklaart u uw toestemming voor het gebruik van alle Diensten. U kunt ook uw
                                            toestemming verklaren door individueel op de schuifregelaars voor elke
                                            categorie cookies te klikken en opslaan.</p>
                                    </div>
                                    <div class="mb-4 border-b">
                                        <h4 class="block mb-2 text-lg font-semibold text-gray-900 ">Essentiele cookies
                                        </h4>
                                        <p class="block mb-4 text-sm text-gray-900">Essentiële cookies worden gebruikt om de basisfunctionaliteiten van de website te waarborgen, zoals navigatie en toegang tot beveiligde gedeelten van de website. Zonder deze cookies kan de website niet naar behoren functioneren.</p>                                        <button v-if="!showEssential" type="button"
                                            class="mb-4 flex flex-row items-center gap-2 text-sm font-medium text-royalblue-500 hover:text-royalblue-500/80"
                                            @click="showEssential = true">
                                            <p>Cookies bekijken</p>
                                            <font-awesome-icon :icon="['far', 'chevron-right']"
                                                class="h-4 w-4 shrink-0" />
                                        </button>
                                        <button v-if="showEssential" type="button"
                                            class="mb-4 flex flex-row items-center gap-2 text-sm font-medium text-royalblue-500 hover:text-royalblue-500/80"
                                            @click="showEssential = false">
                                            <p>Cookies bekijken</p>
                                            <font-awesome-icon :icon="['far', 'chevron-down']"
                                                class="h-4 w-4 shrink-0" />
                                        </button>
                                        <div v-if="showEssential" class="transition-all duration-300 ease-in-out">
                                            <div class="mt-10 ring-1 ring-gray-300 mx-0 rounded-lg overflow-hidden">
                                                <table class="min-w-full divide-y divide-gray-300 border-b">
                                                    <thead class="bg-royalblue-500/5">
                                                        <tr>
                                                            <th scope="col"
                                                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                                                Naam
                                                            </th>
                                                            <th scope="col"
                                                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                                                Expiration
                                                            </th>
                                                            <th scope="col"
                                                                class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                                                Purpose
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody class="divide-y">
                                                        <tr>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                mijnautosnelweg_session
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                2 uur
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                Zorgt dat je dat data veilig verstuurd word
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                XSRF-TOKEN
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                2 uur
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                Zorgt voor een naadloze ervaring
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                fontsCssCache
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                7 dagen
                                                            </td>
                                                            <td
                                                                class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500">
                                                                Geeft aan of de fonts al ingeladen zijn
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mb-4 border-b">
                                        <div class="relative flex justify-between">
                                            <h4 class="block mb-2 text-lg font-semibold text-gray-900 ">Analytics
                                                cookies</h4>
                                            <Switch v-model="acceptAnalytics"
                                                class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none">
                                                <span class="sr-only">Use setting</span>
                                                <span aria-hidden="true"
                                                    class="pointer-events-none absolute h-full w-full rounded-md" />
                                                <span aria-hidden="true"
                                                    :class="[acceptAnalytics ? 'bg-brightorange' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-6 w-12 rounded-full transition-colors duration-200 ease-in-out']" />
                                                <span aria-hidden="true"
                                                    :class="[acceptAnalytics ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                                            </Switch>
                                        </div>
                                        <p class="block mb-4 text-sm text-gray-900">Analytics cookies worden gebruikt om informatie te verzamelen over hoe bezoekers onze website gebruiken. Deze informatie helpt ons om de website te verbeteren door inzicht te krijgen in het gebruik en de prestaties van de site.</p>                                        <button v-if="!showAnalytics" type="button"
                                            class="mb-4 flex flex-row items-center gap-2 text-sm font-medium text-royalblue-500 hover:text-royalblue-500/80"
                                            @click="showAnalytics = true">
                                            <p>Cookies bekijken</p>
                                            <font-awesome-icon :icon="['far', 'chevron-right']"
                                                class="h-4 w-4 shrink-0" />
                                        </button>
                                        <button v-if="showAnalytics" type="button"
                                            class="mb-4 flex flex-row items-center gap-2 text-sm font-medium text-royalblue-500 hover:text-royalblue-500/80"
                                            @click="showAnalytics = false">
                                            <p>Cookies bekijken</p>
                                            <font-awesome-icon :icon="['far', 'chevron-down']"
                                                class="h-4 w-4 shrink-0" />
                                        </button>
                                        <div v-if="showAnalytics" class="transition-all duration-300 ease-in-out">
                                            <table class="mt-4 mb-4 w-full border bg-white rounded-xl overflow-hidden">
                                                <thead class="border-b bg-royalblue-500/5">
                                                    <tr>
                                                        <th class="px-4 py-2 text-left">Naam</th>
                                                        <th class="px-4 py-2 text-left">Expiration</th>
                                                        <th class="px-4 py-2 text-left">Purpose</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>_ga</p>
                                                        </th>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>2 Jaar</p>
                                                        </th>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>Google analytics</p>
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>_ga_KQVNRHT4TX</p>
                                                        </th>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>2 Jaar</p>
                                                        </th>
                                                        <th class="px-4 py-2 font-normal">
                                                            <p>Google analytics</p>
                                                        </th>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </form>
                                <div class="mt-4 flex justify-end items-center space-x-6">
                                    <button @click="acceptCookies" type="button" class="group transition-all duration-300 ease-in-out inline-flex items-center border-b-2 border-transparent pt-1 text-base font-semibold  text-royalblue-500 hover:text-royalblue-500/80">
                                        <span class="bg-left-bottom bg-gradient-to-r from-brightorange to-brightorange bg-[length:0%_2px] bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out">
                                            Opslaan en verder
                                        </span>
                                    </button>
                                    <button type="button"
                                        class="inline-flex w-auto justify-center rounded-md bg-brightorange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightorange/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royalblue-500 sm:col-start-2"
                                        @click="acceptAllCookies">Alles accepteren</button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
